import { useMutation, useQuery, useQueryClient, type UseMutationOptions } from "@tanstack/react-query";
import { z } from "zod";

import { apiGet, apiPost, type RequestOptions } from "./client";
import { okResponseSchema, platformSchema, roleSchema } from "./schemas";

/**
 * /api/v2/auth/* — sozlesme §3.B (backend/app/core/api/auth.py) ile birebir.
 * DIKKAT: /login `active_tenant_id` (sayi|null) doner, /me ise `active_tenant`
 * (nesne|null) — ayni alan farkli sekilde adlandirilir, ikisi de asagida.
 */

export const userSchema = z.object({
  id: z.number(),
  email: z.string(),
  first_name: z.string(),
  last_name: z.string(),
  is_super: z.boolean(),
});
export type UserOut = z.infer<typeof userSchema>;

export const membershipSchema = z.object({
  tenant_id: z.number(),
  slug: z.string(),
  name: z.string(),
  platform: platformSchema,
  role: roleSchema,
  status: z.string(),
});
export type MembershipOut = z.infer<typeof membershipSchema>;

export const tenantBriefSchema = z.object({
  id: z.number(),
  slug: z.string(),
  name: z.string(),
  platform: platformSchema,
});
export type TenantBrief = z.infer<typeof tenantBriefSchema>;

export const loginResponseSchema = z.object({
  user: userSchema,
  tenants: z.array(membershipSchema),
  active_tenant_id: z.number().nullable(),
});
export type LoginResponse = z.infer<typeof loginResponseSchema>;

export const meResponseSchema = z.object({
  user: userSchema,
  tenants: z.array(membershipSchema),
  active_tenant: tenantBriefSchema.nullable(),
  role: roleSchema.nullable(),
});
export type MeResponse = z.infer<typeof meResponseSchema>;

export const switchTenantResponseSchema = z.object({
  active_tenant_id: z.number().nullable(),
  active_tenant: tenantBriefSchema.nullable(),
  role: roleSchema.nullable(),
});
export type SwitchTenantResponse = z.infer<typeof switchTenantResponseSchema>;

export const registerResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
});
export type RegisterResponse = z.infer<typeof registerResponseSchema>;

export interface RegisterPayload {
  email: string;
  password: string;
  first_name?: string;
  last_name?: string;
  tenant_slug: string;
}

export interface ChangePasswordPayload {
  current_password: string;
  new_password: string;
}

// ---------------------------------------------------------------------------
// Istekler
// ---------------------------------------------------------------------------

export function fetchMe(options?: RequestOptions): Promise<MeResponse> {
  return apiGet("/api/v2/auth/me", meResponseSchema, options);
}

export function loginRequest(email: string, password: string): Promise<LoginResponse> {
  // Yanlis sifre/e-posta 401'i bir FORM hatasidir, oturum sonu degil —
  // otomatik /giris yonlendirmesi burada KAPALI.
  return apiPost("/api/v2/auth/login", loginResponseSchema, { email, password }, { noRedirect: true });
}

export function logoutRequest(): Promise<z.infer<typeof okResponseSchema>> {
  return apiPost("/api/v2/auth/logout", okResponseSchema, undefined, { noRedirect: true });
}

export function switchTenantRequest(tenantId: number): Promise<SwitchTenantResponse> {
  return apiPost("/api/v2/auth/switch-tenant", switchTenantResponseSchema, { tenant_id: tenantId });
}

export function registerRequest(payload: RegisterPayload): Promise<RegisterResponse> {
  return apiPost("/api/v2/auth/register", registerResponseSchema, payload, { noRedirect: true });
}

export function changePasswordRequest(payload: ChangePasswordPayload): Promise<z.infer<typeof okResponseSchema>> {
  return apiPost("/api/v2/auth/change-password", okResponseSchema, payload);
}

// ---------------------------------------------------------------------------
// TanStack Query kancalari
// ---------------------------------------------------------------------------

export const AUTH_ME_QUERY_KEY = ["auth", "me"] as const;

export function useMeQuery(enabled = true) {
  return useQuery({
    queryKey: AUTH_ME_QUERY_KEY,
    queryFn: () => fetchMe({ noRedirect: true }),
    enabled,
    retry: false,
    staleTime: 60_000,
  });
}

export function useLoginMutation(
  options?: UseMutationOptions<LoginResponse, unknown, { email: string; password: string }>,
) {
  return useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) => loginRequest(email, password),
    ...options,
  });
}

export function useLogoutMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: logoutRequest,
    onSettled: () => {
      queryClient.setQueryData(AUTH_ME_QUERY_KEY, undefined);
      void queryClient.invalidateQueries({ queryKey: AUTH_ME_QUERY_KEY });
    },
  });
}

export function useSwitchTenantMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (tenantId: number) => switchTenantRequest(tenantId),
    onSuccess: async () => {
      // Mağaza verilerinin anahtarları tenant içermiyor. Yeni mağazaya
      // geçmeden önce eski istekleri iptal edip önbelleği temizleyin.
      await queryClient.cancelQueries();
      queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== "auth" });
    },
  });
}

export function useRegisterMutation() {
  return useMutation({ mutationFn: registerRequest });
}

export function useChangePasswordMutation() {
  return useMutation({ mutationFn: changePasswordRequest });
}
